import type { Directive, DirectiveBinding } from 'vue';

export interface TicketTimelineAnimateOptions {
  root?: Element | null;
  threshold?: number;
  enterClass?: string;
  ready?: boolean;
}

interface ElementState {
  observer: IntersectionObserver | null;
  revealed: boolean;
  readyApplied: boolean;
}

const elementState = new WeakMap<HTMLElement, ElementState>();

function splitClasses(classNames: string): string[] {
  return classNames.split(/\s+/).filter(Boolean);
}

function playEnterAnimation(el: HTMLElement, enterClass: string): void {
  const classes = splitClasses(enterClass);
  if (classes.length === 0) {
    el.style.opacity = '';
    return;
  }

  el.style.opacity = '0';
  requestAnimationFrame(() => {
    el.style.opacity = '';
    el.classList.add(...classes);
    const onEnd = (): void => {
      el.classList.remove(...classes);
      el.removeEventListener('animationend', onEnd);
    };
    el.addEventListener('animationend', onEnd);
  });
}

function isElementIntersectingRoot(
  el: Element,
  root: Element | null,
  threshold: number
): boolean {
  const elRect = el.getBoundingClientRect();
  const rootRect =
    root?.getBoundingClientRect() ??
    ({
      top: 0,
      left: 0,
      right: window.innerWidth,
      bottom: window.innerHeight
    } as DOMRect);

  const intersectionTop = Math.max(elRect.top, rootRect.top);
  const intersectionBottom = Math.min(elRect.bottom, rootRect.bottom);
  const intersectionHeight = Math.max(0, intersectionBottom - intersectionTop);
  const elHeight = elRect.height;

  if (elHeight <= 0) {
    return false;
  }

  return intersectionHeight / elHeight >= threshold;
}

function startObserver(
  el: HTMLElement,
  options: TicketTimelineAnimateOptions
): void {
  const state = elementState.get(el);
  if (!state || state.observer) {
    return;
  }

  const root = options.root ?? null;
  const threshold = options.threshold ?? 0.15;
  const enterClass = options.enterClass ?? '';

  state.observer = new IntersectionObserver(
    (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || state.revealed) {
          continue;
        }

        state.revealed = true;
        if (enterClass) {
          playEnterAnimation(el, enterClass);
        } else {
          el.style.opacity = '';
        }
        state.observer?.unobserve(el);
      }
    },
    { root, threshold }
  );

  state.observer.observe(el);
}

function applyReadyState(
  el: HTMLElement,
  options: TicketTimelineAnimateOptions
): void {
  const state = elementState.get(el);
  if (!state || state.readyApplied || !options.ready) {
    return;
  }

  state.readyApplied = true;
  const root = options.root ?? null;
  const threshold = options.threshold ?? 0.15;
  const intersecting = isElementIntersectingRoot(el, root, threshold);

  if (intersecting) {
    state.revealed = true;
    el.style.opacity = '';
  } else {
    state.revealed = false;
    el.style.opacity = '0';
  }

  startObserver(el, options);
}

function cleanupElement(el: HTMLElement): void {
  const state = elementState.get(el);
  state?.observer?.disconnect();
  elementState.delete(el);
}

function resolveOptions(
  binding: DirectiveBinding<TicketTimelineAnimateOptions | undefined>
): TicketTimelineAnimateOptions {
  return binding.value ?? {};
}

export const ticketTimelineAnimate: Directive<
  HTMLElement,
  TicketTimelineAnimateOptions | undefined
> = {
  mounted(el: HTMLElement, binding) {
    elementState.set(el, {
      observer: null,
      revealed: false,
      readyApplied: false
    });
    applyReadyState(el, resolveOptions(binding));
  },
  updated(el: HTMLElement, binding) {
    applyReadyState(el, resolveOptions(binding));
  },
  unmounted(el: HTMLElement) {
    cleanupElement(el);
  }
};
